import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

import { ApiResponseError } from './ApiResponseError';
import { parseApiError } from './parseApiError';

import type {
  ApiError,
  AxiosRequestOptions,
  AxiosSuccessResponse,
} from './types';

/**
 * Текст, когда запрос ушёл, но ответа не было вовсе.
 */
export const NO_RESPONSE_MESSAGE =
  'Сервер не отвечает. Запрос был отправлен, но ответ не получен.';

/**
 * Код, которым обозначается отсутствие ответа: настоящего статуса нет.
 */
/**
 * Код, который клиент ставит СЕБЕ САМ, когда запрос ушёл, а ответа не было
 * вовсе. Экспортируется: по нему потребитель отличает отказ транспорта от
 * решения сервера — `deals` через `isTransientFailure`, `analyst` через тег
 * причины в Sentry (AF-537). Без экспорта число расходится копиями.
 */
export const NO_RESPONSE_CODE = 503;

const DEFAULT_TIMEOUT_MS = 60_000;

/**
 * У этих методов payload уходит в query-параметры, а не в тело.
 */
const QUERY_PARAMETER_METHODS = new Set(['get', 'delete']);

export type ApiClientOptions = {
  /**
   * Транспорт. Он же отдаётся наружу для кодогена — см. AF-14.
   */
  httpClient: AxiosInstance;
  /**
   * Что делать при 401. Приложение обычно разлогинивает пользователя и
   * перезагружает страницу.
   *
   * Клиент гарантирует, что обработчик не вызовется повторно, пока он ещё
   * работает: иначе запрос на `/auth/force-logout`, сам отвечающий 401,
   * поднимал бы его заново — и так до переполнения стека.
   */
  onUnauthorized?: () => Promise<void> | void;
};

export const createApiClient = (options: ApiClientOptions) => {
  const { httpClient, onUnauthorized } = options;

  /**
   * Признак «разлогинивание уже идёт». Не булев флаг, а промис: он нужен и
   * чтобы отсечь повторный вызов, и чтобы снять признак ровно по завершении,
   * чем бы оно ни кончилось.
   */
  let logoutInFlight: Promise<void> | undefined;

  const handleUnauthorized = async (): Promise<void> => {
    if (!onUnauthorized) return;
    // Уже разлогиниваемся. Именно `return`, а не `await logoutInFlight`:
    // сюда попадает в том числе 401 от самого разлогинивающего запроса,
    // а он выполняется внутри этого же промиса — ожидание было бы дедлоком.
    if (logoutInFlight) return;

    // `.then(() => {})` здесь стоял, чтобы привести `Promise<void> | void` к
    // `Promise<void>`. Приводить нечего: `Promise.resolve()` уже даёт
    // `Promise<void>` для обоих вариантов, а пустой `then` линтер справедливо
    // считает разрывом цепочки (`promise/always-return`).
    logoutInFlight = Promise.resolve(onUnauthorized()).finally(() => {
      logoutInFlight = undefined;
    });

    await logoutInFlight;
  };

  const makeAxiosRequest = async <
    ResponseData,
    PayloadData extends Record<string, unknown> = NonNullable<unknown>,
  >({
    url,
    method,
    payload,
    deletePayloadInBody = false,
    contentType = 'application/json',
    timeout = DEFAULT_TIMEOUT_MS,
    responseType,
    onUploadProgress,
  }: AxiosRequestOptions<PayloadData>): Promise<ResponseData> => {
    const dataOrParameters = QUERY_PARAMETER_METHODS.has(method) && !(method==='delete'&&deletePayloadInBody)
      ? 'params'
      : 'data';

    const baseConfig: AxiosRequestConfig = {
      url,
      method,
      timeout,
      ...(responseType && { responseType }),
      ...(onUploadProgress && { onUploadProgress }),
    };

    const config = Object.assign(
      baseConfig,
      payload && { [dataOrParameters]: payload },
      contentType && { headers: { 'Content-Type': contentType } },
    );

    try {
      const response: AxiosSuccessResponse<ResponseData> =
        await httpClient.request<ResponseData>(config);
      return response.data;
    } catch (error) {
      if (!axios.isAxiosError<ApiError>(error)) {
        // Ошибка не транспортная — что-то сломалось при подготовке запроса.
        throw error;
      }

      if (error.response) {
        if (error.response.status === 401) {
          /*
           * Отказ САМОГО разлогинивания не должен подменять исходную ошибку
           * (AF-537, adversarial-ревью).
           *
           * `handleUnauthorized` уходит в сеть, и если ответа на него не
           * пришло, его собственная `ApiResponseError` с кодом 503 вылетала
           * отсюда ВМЕСТО исходного 401. Вызывающий получал «сервер не
           * отвечает» на запрос, на который сервер ответил — и, что хуже,
           * такой 503 выглядит временным сбоем, поэтому повторяемый запрос
           * уходил на второй круг и слал второй разлогинивающий POST
           * (`logoutInFlight` снимается в `finally`).
           *
           * Для вызывающего истина — ответ на ЕГО запрос. Неудача побочного
           * действия гасится здесь намеренно.
           */
          await handleUnauthorized().catch(() => {});
        }

        const body = error.response.data;
        const parsed = parseApiError(body);

        throw new ApiResponseError({
          type: error.code,
          code: error.response.status,
          message: parsed.message,
          fields: parsed.fields,
          status: error.response.statusText,
          data:
            body && typeof body === 'object'
              ? (body as Record<string, unknown>)
              : undefined,
        });
      }

      /*
       * `noResponse` — явный признак, а не вывод из кода. Числа 503 для этого
       * НЕ ХВАТАЕТ: ровно тот же код приходит и настоящим ответом сервера
       * (`503 Service Unavailable`), и различить их по числу нельзя (AF-537).
       */
      throw new ApiResponseError({
        type: error.code,
        code: NO_RESPONSE_CODE,
        status: error.name,
        message: NO_RESPONSE_MESSAGE,
        noResponse: true,
      });
    }
  };

  return { makeAxiosRequest, httpClient };
};

export type ApiClient = ReturnType<typeof createApiClient>;
